import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import { Joyride, STATUS } from 'react-joyride';
import { serviceRequestsAPI, documentsAPI, paymentsAPI } from '@/lib/api';
import { FileText, CreditCard, Clock, CheckCircle, AlertCircle, Folder, ArrowRight, Upload, MessageSquare } from 'lucide-react';

const DashboardPage = () => {
  const { user } = useAuth();
  const [serviceRequests, setServiceRequests] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [removing, setRemoving] = useState(null);
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [ticketRequestId, setTicketRequestId] = useState(null);
  const [ticketSubject, setTicketSubject] = useState('');
  const [ticketMessage, setTicketMessage] = useState('');
  const [ticketSubmitting, setTicketSubmitting] = useState(false);
  const [runTour, setRunTour] = useState(false);
  const [myTickets, setMyTickets] = useState([]);
  const [showMyTicketsModal, setShowMyTicketsModal] = useState(false);
  const [selectedTicketForReply, setSelectedTicketForReply] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [replyingToTicket, setReplyingToTicket] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPaymentRequest, setSelectedPaymentRequest] = useState(null);
  const [transactionId, setTransactionId] = useState('');
  const [confirmingPayment, setConfirmingPayment] = useState(false);


  const tourSteps = [
    {
      target: 'body',
      content: <div className="text-left">
        <h3 className="text-lg font-bold mb-2">Welcome to ITRQUICK Dashboard! 🎉</h3>
        <p>Let us show you around and help you get started with your tax filing journey.</p>
      </div>,
      placement: 'center',
      disableBeacon: true,
    },
    {
      target: '[data-testid="nav-dashboard"]',
      content: <div className="text-left">
        <h4 className="font-bold mb-1">Dashboard (Green)</h4>
        <p>Your main hub to view all your service requests, upload documents, and make payments.</p>
      </div>,
    },
    {
      target: '[data-testid="nav-tax-locker"]',
      content: <div className="text-left">
        <h4 className="font-bold mb-1">Tax Locker (Red)</h4>
        <p><strong>All your files uploaded can be seen here.</strong> You can also add password protection to each document!</p>
      </div>,
    },
    {
      target: '.bg-gradient-to-r',
      content: <div className="text-left">
        <h4 className="font-bold mb-1">Quick Stats</h4>
        <p>See your total service requests, pending payments, and uploaded documents at a glance.</p>
      </div>,
    },
    {
      target: '[data-testid^="service-request-"]',
      content: <div className="text-left">
        <h4 className="font-bold mb-1">Service Requests</h4>
        <p>Each card shows your service details, payment status, and actions you can take like uploading documents or raising tickets.</p>
      </div>,
    },
  ];
  const [bulkDeleteDialog, setBulkDeleteDialog] = useState(false);

  const loadDashboardData = useCallback(async (isRetry = false) => {
    try {
      if (!isRetry) setLoading(true);
      setError(null);
      
      const [requestsRes, docsRes, paymentsRes] = await Promise.all([
        serviceRequestsAPI.getAll(),
        documentsAPI.getAll(),
        paymentsAPI.getAll()
      ]);
      
      // Sort service requests by created_at (newest first)
      const sortedRequests = requestsRes.data.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      
      setServiceRequests(sortedRequests);
      setDocuments(docsRes.data);
      setPayments(paymentsRes.data);
      setRetryCount(0); // Reset retry count on success
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError(err.message || 'Failed to load data');
      
      // Auto-retry up to 2 times with exponential backoff
      if (retryCount < 2) {
        const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s
        console.log(`Retrying in ${delay}ms... (attempt ${retryCount + 1}/2)`);
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
          loadDashboardData(true);
        }, delay);
      }
    } finally {
      setLoading(false);
    }
  }, [retryCount]);

  useEffect(() => {
    loadDashboardData();
    
    // Check if this is user's first visit
    const hasSeenTour = localStorage.getItem('dashboard_tour_completed');
    if (!hasSeenTour && serviceRequests.length > 0) {
      // Delay tour to let page render
      setTimeout(() => setRunTour(true), 1000);
    }
  }, [loadDashboardData, serviceRequests.length]);

  const handleJoyrideCallback = (data) => {
    const { status } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      localStorage.setItem('dashboard_tour_completed', 'true');
      setRunTour(false);
    }
  };

  const handleRemoveService = async (requestId) => {
    setServiceToDelete(requestId);
    setShowDeleteDialog(true);
  };

  const confirmDeleteService = async () => {
    if (!serviceToDelete) return;
    
    setRemoving(serviceToDelete);
    try {
      await serviceRequestsAPI.delete(serviceToDelete);
      setServiceRequests(prev => prev.filter(req => req.id !== serviceToDelete));
      setSelectedServices(prev => prev.filter(id => id !== serviceToDelete));
    } catch (err) {
      console.error('Error removing service:', err);
      alert(err.response?.data?.detail || 'Failed to remove service. Please try again.');
    } finally {
      setRemoving(null);
      setShowDeleteDialog(false);
      setServiceToDelete(null);
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedServices([]);
      setSelectAll(false);
    } else {
      // Only select services that are not paid (deletable services)
      const deletableServices = serviceRequests
        .filter(req => req.payment_status !== 'paid')
        .map(req => req.id);
      setSelectedServices(deletableServices);
      setSelectAll(true);
    }
  };

  const handleCheckboxChange = (requestId) => {
    if (selectedServices.includes(requestId)) {
      setSelectedServices(prev => prev.filter(id => id !== requestId));
    } else {
      setSelectedServices(prev => [...prev, requestId]);
    }
  };

  const handleRemoveSelected = async () => {
    if (selectedServices.length === 0) {
      alert('Please select at least one service to remove');
      return;
    }

    setBulkDeleteDialog(true);
  };

  const confirmBulkDelete = async () => {
    try {
      setRemoving('batch');
      
      // Delete each selected service
      const deletePromises = selectedServices.map(id => serviceRequestsAPI.delete(id));
      await Promise.all(deletePromises);
      
      setServiceRequests(prev => prev.filter(req => !selectedServices.includes(req.id)));
      setSelectedServices([]);
      setSelectAll(false);
    } catch (err) {
      console.error('Error removing services:', err);
      alert('Failed to remove some services. Please try again.');
    } finally {
      setRemoving(null);
      setBulkDeleteDialog(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="text-green-500" size={20} />;
      case 'in_progress':
      case 'documents_received':
      case 'review_pending':
        return <Clock className="text-yellow-500" size={20} />;
      default:
        return <AlertCircle className="text-gray-400" size={20} />;
    }
  };

  const getStatusText = (status) => {
    if (status === 'paid') return 'Paid';
    if (status === 'under_review') return 'Payment Under Review';
    return status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const handleRaiseTicket = (requestId) => {
    setTicketRequestId(requestId);
    setShowTicketModal(true);
  };

  const handleTicketSubmit = async () => {
    if (!ticketSubject.trim() || !ticketMessage.trim()) {
      alert('Please fill in both subject and message');
      return;
    }

    setTicketSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tickets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          service_request_id: ticketRequestId,
          subject: ticketSubject,
          message: ticketMessage
        })
      });

      if (response.ok) {
        alert('Ticket raised successfully! We will respond shortly.');
        setShowTicketModal(false);
        setTicketSubject('');
        setTicketMessage('');
        setTicketRequestId(null);
      } else {
        const error = await response.json();
        alert(`Failed to raise ticket: ${error.detail || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error raising ticket:', err);
      alert('Failed to raise ticket. Please try again.');
    } finally {
      setTicketSubmitting(false);
    }
  };

  const loadMyTickets = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tickets`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });



  const handlePayNowClick = (request) => {
    setSelectedPaymentRequest(request);
    setTransactionId('');
    setShowPaymentModal(true);
  };

  const handleConfirmPayment = async () => {
    if (!transactionId.trim()) {
      alert('Please enter the transaction ID');
      return;
    }

    setConfirmingPayment(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/service-requests/${selectedPaymentRequest.id}/confirm-payment`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            transaction_id: transactionId
          })
        }
      );

      if (response.ok) {
        alert('✓ Payment confirmed! Your payment is under review. You will be notified once verified.');
        setShowPaymentModal(false);
        setTransactionId('');
        // Reload data
        loadData();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to confirm payment');
      }
    } catch (error) {
      console.error('Error confirming payment:', error);
      alert(error.message || 'Failed to confirm payment. Please try again.');
    } finally {
      setConfirmingPayment(false);
    }
  };

      if (response.ok) {
        const tickets = await response.json();
        setMyTickets(tickets);
      }
    } catch (err) {
      console.error('Error loading tickets:', err);
    }
  };

  const handleOpenMyTickets = () => {
    loadMyTickets();
    setShowMyTicketsModal(true);
  };

  const handleReplyToTicket = async () => {
    if (!selectedTicketForReply || !replyMessage.trim()) {
      alert('Please enter a reply message');
      return;
    }

    setReplyingToTicket(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/tickets/${selectedTicketForReply.id}/reply`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            reply: replyMessage
          })
        }
      );

      if (response.ok) {
        alert('Reply sent successfully!');
        setReplyMessage('');
        setSelectedTicketForReply(null);
        // Reload tickets
        await loadMyTickets();
      } else {
        const error = await response.json();
        alert(`Failed to send reply: ${error.detail || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error sending reply:', err);
      alert('Failed to send reply. Please try again.');
    } finally {
      setReplyingToTicket(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading your dashboard...</p>
            {retryCount > 0 && (
              <p className="text-sm text-gray-500 mt-2">Retrying... (Attempt {retryCount}/2)</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Joyride Tour */}
      <Joyride
        steps={tourSteps}
        run={runTour}
        continuous
        showProgress
        showSkipButton
        callback={handleJoyrideCallback}
        styles={{
          options: {
            primaryColor: '#7c3aed',
            zIndex: 10000,
          },
          tooltip: {
            fontSize: 14,
          },
          buttonNext: {
            backgroundColor: '#7c3aed',
          },
          buttonBack: {
            color: '#7c3aed',
          },
        }}
        locale={{
          last: 'Finish',
          skip: 'Skip Tour',
        }}
      />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Error Banner */}
        {error && retryCount >= 2 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertCircle className="text-red-600" size={20} />
                <div>
                  <p className="text-red-800 font-medium">Failed to load dashboard data</p>
                  <p className="text-red-600 text-sm">Please check your connection and try again</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setRetryCount(0);
                  loadDashboardData();
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm"
              >
                Retry Now
              </button>
            </div>
          </div>
        )}
        
        {/* Welcome Section */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 text-white mb-8" data-testid="welcome-banner">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">Welcome back, {user.name}!</h1>
              <p className="text-blue-100 mb-4">Client ID: {user.client_id}</p>
              <div className="flex gap-3">
                <Link 
                  to="/services" 
                  className="inline-flex items-center gap-2 bg-orange-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-600 transition shadow-lg shadow-orange-500/50 animate-[pulse_0.8s_ease-in-out_infinite]"
                  data-testid="start-new-service-button"
                >
                  Start New Service <ArrowRight size={20} />
                </Link>
                <button
                  onClick={() => {
                    localStorage.removeItem('dashboard_tour_completed');
                    setRunTour(true);
                  }}
                  className="inline-flex items-center gap-2 bg-blue-500 text-white px-4 py-3 rounded-lg font-medium hover:bg-blue-400 transition"
                >
                  📚 Take Tour
                </button>
                <button
                  onClick={handleOpenMyTickets}
                  className="inline-flex items-center gap-2 bg-purple-500 text-white px-4 py-3 rounded-lg font-medium hover:bg-purple-400 transition"
                >
                  <MessageSquare size={18} />
                  My Tickets
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="stat-active-requests">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm mb-1">Active Requests</p>
                <p className="text-3xl font-bold text-gray-900">{serviceRequests.length}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-lg">
                <FileText className="text-blue-600" size={28} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="stat-documents">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm mb-1">Documents</p>
                <p className="text-3xl font-bold text-gray-900">{documents.length}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-lg">
                <Folder className="text-green-600" size={28} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="stat-payments">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm mb-1">Payments Made</p>
                <p className="text-3xl font-bold text-gray-900">{payments.filter(p => p.status === 'completed').length}</p>
              </div>
              <div className="bg-purple-100 p-3 rounded-lg">
                <CreditCard className="text-purple-600" size={28} />
              </div>
            </div>
          </div>
        </div>

        {/* Service Requests */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8" data-testid="service-requests-section">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold">Your Service Requests</h2>
            {serviceRequests.length > 0 && (
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer" data-testid="select-all-checkbox">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                    className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="font-medium">Select All</span>
                </label>
                {selectedServices.length > 0 && (
                  <button
                    onClick={handleRemoveSelected}
                    disabled={removing === 'batch'}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-red-700 transition disabled:opacity-50"
                    data-testid="remove-selected-button"
                  >
                    {removing === 'batch' ? 'Removing...' : `Remove Selected (${selectedServices.length})`}
                  </button>
                )}
              </div>
            )}
          </div>
          
          {serviceRequests.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="mx-auto text-gray-400 mb-4" size={48} />
              <p className="text-gray-600 mb-4">No service requests yet</p>
              <Link to="/services" className="text-blue-600 hover:text-blue-700 font-semibold" data-testid="browse-services-link">
                Browse Services
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {serviceRequests.map((request) => (
                <div key={request.id} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition" data-testid={`service-request-${request.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <input
                        type="checkbox"
                        checked={selectedServices.includes(request.id)}
                        onChange={() => handleCheckboxChange(request.id)}
                        disabled={request.payment_status === 'paid'}
                        className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-30 disabled:cursor-not-allowed"
                        data-testid={`checkbox-${request.id}`}
                      />
                      {getStatusIcon(request.status)}
                      <div>
                        <h3 className="font-semibold text-lg">{request.service_name}</h3>
                        <p className="text-sm text-gray-600 font-mono">Request ID: {request.id}</p>
                        {request.created_at && (
                          <p className="text-xs text-gray-500">
                            Ordered: {new Date(request.created_at).toLocaleString('en-IN', { 
                              dateStyle: 'medium', 
                              timeStyle: 'short',
                              timeZone: 'Asia/Kolkata'
                            })}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="text-sm font-medium text-gray-700">{getStatusText(request.status)}</div>
                        <div className="text-xs text-gray-500">Payment: {getStatusText(request.payment_status)}</div>
                        {request.custom_amount && (
                          <div className="text-sm font-bold text-orange-600 mt-1">
                            Amount: ₹{request.custom_amount}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {/* Show Upload button first for unpaid services with no documents */}
                        {request.payment_status === 'pending' && request.documents.length === 0 && (
                          <>
                            <Link 
                              to={`/service-request/${request.id}/upload`}
                              className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-700 transition flex items-center gap-2"
                              data-testid={`upload-docs-${request.id}`}
                            >
                              <Upload size={16} />
                              Upload Documents
                            </Link>
                            {request.payment_link ? (
                              // Pay Fees button - no animation, works like Pay Now
                              <Link
                                to={`/service-request/${request.id}/payment`}
                                className="bg-orange-500 text-white px-4 py-2 rounded-lg text-sm hover:bg-orange-600 transition font-semibold"
                                data-testid={`pay-fees-${request.id}`}
                              >
                                💳 Pay Fees ₹{request.custom_amount}
                              </Link>
                            ) : null}
                            <button
                              onClick={() => handleRemoveService(request.id)}
                              disabled={removing === request.id}
                              className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-red-700 transition disabled:opacity-50"
                              data-testid={`remove-service-${request.id}`}
                            >
                              {removing === request.id ? 'Removing...' : 'Remove'}
                            </button>
                          </>
                        )}
                        {/* Show Upload More & Pay Now after some documents uploaded but before payment */}
                        {request.payment_status === 'pending' && request.documents.length > 0 && (
                          <>
                            <Link 
                              to={`/service-request/${request.id}/upload`}
                              className="bg-gray-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-gray-700 transition flex items-center gap-2"
                              data-testid={`upload-more-${request.id}`}
                            >
                              <Upload size={16} />
                              Upload More
                            </Link>
                            {request.payment_link ? (
                              // Pay Fees link - no animation, works like Pay Now
                              <Link
                                to={`/service-request/${request.id}/payment`}
                                className="bg-orange-500 text-white px-4 py-2 rounded-lg text-sm hover:bg-orange-600 transition font-semibold"
                                data-testid={`pay-fees-${request.id}`}
                              >
                                💳 Pay Fees ₹{request.custom_amount}
                              </Link>
                            ) : (
                              // Show regular payment page link
                              <Link 
                                to={`/service-request/${request.id}/payment`}
                                className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 transition"
                                data-testid={`pay-now-${request.id}`}
                              >
                                Pay Now
                              </Link>
                            )}
                            <button
                              onClick={() => handleRemoveService(request.id)}
                              disabled={removing === request.id}
                              className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-red-700 transition disabled:opacity-50"
                              data-testid={`remove-service-${request.id}`}
                            >
                              {removing === request.id ? 'Removing...' : 'Remove'}
                            </button>
                          </>
                        )}
                        {/* Payment Under Review - Show info message */}
                        {request.payment_status === 'under_review' && (
                          <div className="bg-blue-50 border border-blue-300 rounded-lg px-4 py-2 text-sm">
                            <p className="text-blue-800 font-medium">⏳ Payment Under Review</p>
                            <p className="text-blue-600 text-xs mt-1">We're verifying your payment. You'll be notified soon.</p>
                          </div>
                        )}
                        {/* After payment, only show Upload if no documents yet */}
                        {request.payment_status === 'paid' && request.documents.length === 0 && (
                          <>
                            <Link 
                              to={`/service-request/${request.id}/upload`}
                              className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-700 transition flex items-center gap-2"
                              data-testid={`upload-docs-${request.id}`}
                            >
                              <Upload size={16} />
                              Upload Documents
                            </Link>
                            {request.payment_link ? (
                              <Link
                                to={`/service-request/${request.id}/payment`}
                                className="bg-orange-500 text-white px-4 py-2 rounded-lg text-sm hover:bg-orange-600 transition font-semibold"
                                data-testid={`pay-fees-${request.id}`}
                              >
                                💳 Pay Fees ₹{request.custom_amount}
                              </Link>
                            ) : (
                              <button
                                onClick={() => handleRaiseTicket(request.id)}
                                className="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-purple-700 transition flex items-center gap-2"
                              >
                                <MessageSquare size={16} />
                                Raise Ticket
                              </button>
                            )}
                          </>
                        )}
                        {/* After payment and documents uploaded, show Raise Ticket or Pay Extra */}
                        {request.payment_status === 'paid' && request.documents.length > 0 && (
                          <>
                            {request.payment_link ? (
                              <Link
                                to={`/service-request/${request.id}/payment`}
                                className="bg-orange-500 text-white px-4 py-2 rounded-lg text-sm hover:bg-orange-600 transition font-semibold"
                                data-testid={`pay-fees-${request.id}`}
                              >
                                💳 Pay Fees ₹{request.custom_amount}
                              </Link>
                            ) : null}
                            <button
                              onClick={() => handleRaiseTicket(request.id)}
                              className="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-purple-700 transition flex items-center gap-2"
                            >
                              <MessageSquare size={16} />
                              Raise Ticket
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-3 gap-6">
          <Link 
            to="/services"
            className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 hover:border-blue-400 transition group"
            data-testid="quick-action-services"
          >
            <FileText className="text-blue-600 mb-4 group-hover:scale-110 transition" size={32} />
            <h3 className="font-semibold text-lg mb-2">Browse Services</h3>
            <p className="text-gray-600 text-sm">Explore ITR filing, GST, and more</p>
          </Link>

          <Link 
            to="/tax-locker"
            className="bg-green-50 border-2 border-green-200 rounded-xl p-6 hover:border-green-400 transition group"
            data-testid="quick-action-tax-locker"
          >
            <Folder className="text-green-600 mb-4 group-hover:scale-110 transition" size={32} />
            <h3 className="font-semibold text-lg mb-2">Tax Locker</h3>
            <p className="text-gray-600 text-sm">Access your stored documents</p>
          </Link>

          <Link 
            to="/profile"
            className="bg-purple-50 border-2 border-purple-200 rounded-xl p-6 hover:border-purple-400 transition group"
            data-testid="quick-action-profile"
          >
            <CreditCard className="text-purple-600 mb-4 group-hover:scale-110 transition" size={32} />
            <h3 className="font-semibold text-lg mb-2">My Profile</h3>
            <p className="text-gray-600 text-sm">View and update your details</p>
          </Link>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold mb-2">Remove Service?</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to remove this service? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteDialog(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteService}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Dialog */}
      {bulkDeleteDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold mb-2">Remove {selectedServices.length} Service(s)?</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to remove {selectedServices.length} selected service(s)? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setBulkDeleteDialog(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmBulkDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Remove All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ticket Modal */}
      {showTicketModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full p-6">
            <h2 className="text-2xl font-bold mb-4">Raise Support Ticket</h2>
            <p className="text-gray-600 mb-4 text-sm">
              Request ID: <code className="bg-gray-100 px-2 py-1 rounded font-mono text-xs">{ticketRequestId}</code>
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
                <input
                  type="text"
                  value={ticketSubject}
                  onChange={(e) => setTicketSubject(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Brief description of your issue"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                <textarea
                  value={ticketMessage}
                  onChange={(e) => setTicketMessage(e.target.value)}
                  rows="6"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Describe your issue in detail..."
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleTicketSubmit}
                disabled={ticketSubmitting}
                className="flex-1 bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition disabled:opacity-50"
              >
                {ticketSubmitting ? 'Submitting...' : 'Submit Ticket'}
              </button>
              <button
                onClick={() => {
                  setShowTicketModal(false);
                  setTicketSubject('');
                  setTicketMessage('');
                  setTicketRequestId(null);
                }}
                className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* My Tickets Modal */}
      {showMyTicketsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-auto">
            <div className="sticky top-0 bg-white border-b p-6 flex items-center justify-between">
              <h2 className="text-2xl font-bold">My Support Tickets</h2>
              <button
                onClick={() => {
                  setShowMyTicketsModal(false);
                  setSelectedTicketForReply(null);
                  setReplyMessage('');
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="p-6">
              {myTickets.length === 0 ? (
                <div className="text-center py-12">
                  <MessageSquare className="mx-auto mb-4 text-gray-400" size={64} />
                  <p className="text-gray-600">You haven't raised any tickets yet</p>
                  <button
                    onClick={() => {
                      setShowMyTicketsModal(false);
                    }}
                    className="mt-4 text-purple-600 hover:underline"
                  >
                    Raise your first ticket from any service request
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {myTickets.map((ticket) => (
                    <div
                      key={ticket.id}
                      className={`border rounded-lg p-4 ${
                        ticket.status === 'open' || ticket.status === 'customer_replied'
                          ? 'border-orange-300 bg-orange-50'
                          : 'border-green-300 bg-green-50'
                      }`}
                    >
                      <div className="mb-3">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-lg">{ticket.subject}</h3>
                          <span
                            className={`text-xs px-2 py-1 rounded-full ${
                              ticket.status === 'open'
                                ? 'bg-orange-200 text-orange-800'
                                : ticket.status === 'responded'
                                ? 'bg-green-200 text-green-800'
                                : 'bg-blue-200 text-blue-800'
                            }`}
                          >
                            {ticket.status === 'open' ? '🔔 Waiting for Admin' : 
                             ticket.status === 'responded' ? '✓ Admin Responded' : 
                             '💬 You Replied'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">
                          Request ID: <code className="bg-gray-200 px-2 py-0.5 rounded text-xs">{ticket.service_request_id}</code>
                        </p>
                        <p className="text-xs text-gray-500">
                          Created: {new Date(ticket.created_at).toLocaleString('en-IN', { 
                            dateStyle: 'medium', 
                            timeStyle: 'short',
                            timeZone: 'Asia/Kolkata'
                          })}
                        </p>
                      </div>

                      <div className="space-y-3">
                        {/* Your Message */}
                        <div className="bg-white p-3 rounded border">
                          <p className="text-sm font-medium text-gray-700 mb-1">Your Message:</p>
                          <p className="text-gray-700">{ticket.message}</p>
                        </div>

                        {/* Admin Response */}
                        {ticket.admin_response && (
                          <div className="bg-blue-50 border border-blue-200 rounded p-3">
                            <p className="text-sm font-medium text-blue-800 mb-1">Admin Response:</p>
                            <p className="text-gray-700">{ticket.admin_response}</p>
                            <p className="text-xs text-blue-700 mt-2">
                              {new Date(ticket.admin_response_at).toLocaleString('en-IN', {
                                dateStyle: 'medium',
                                timeStyle: 'short',
                                timeZone: 'Asia/Kolkata'
                              })}
                            </p>
                          </div>
                        )}

                        {/* Customer Reply */}
                        {ticket.customer_reply && (
                          <div className="bg-purple-50 border border-purple-200 rounded p-3">
                            <p className="text-sm font-medium text-purple-800 mb-1">Your Follow-up:</p>
                            <p className="text-gray-700">{ticket.customer_reply}</p>
                            <p className="text-xs text-purple-700 mt-2">
                              {new Date(ticket.customer_reply_at).toLocaleString('en-IN', {
                                dateStyle: 'medium',
                                timeStyle: 'short',
                                timeZone: 'Asia/Kolkata'
                              })}
                            </p>
                          </div>
                        )}

                        {/* Reply Button (only if admin has responded and customer hasn't replied yet) */}
                        {ticket.admin_response && !ticket.customer_reply && (
                          <div>
                            {selectedTicketForReply?.id === ticket.id ? (
                              <div className="bg-gray-50 border rounded p-3">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Your Reply:
                                </label>
                                <textarea
                                  value={replyMessage}
                                  onChange={(e) => setReplyMessage(e.target.value)}
                                  rows="3"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                  placeholder="Type your follow-up message..."
                                />
                                <div className="flex gap-2 mt-3">
                                  <button
                                    onClick={handleReplyToTicket}
                                    disabled={replyingToTicket || !replyMessage.trim()}
                                    className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition disabled:opacity-50"
                                  >
                                    {replyingToTicket ? 'Sending...' : 'Send Reply'}
                                  </button>
                                  <button
                                    onClick={() => {
                                      setSelectedTicketForReply(null);
                                      setReplyMessage('');
                                    }}
                                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <button
                                onClick={() => setSelectedTicketForReply(ticket)}
                                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition text-sm flex items-center gap-2"
                              >
                                <MessageSquare size={16} />
                                Reply to Admin
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Payment QR Modal */}
      {showPaymentModal && selectedPaymentRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="mb-6">
              <h2 className="text-2xl font-bold mb-2 text-gray-800 flex items-center gap-2">
                📱 Scan QR Code to Pay
              </h2>
              <p className="text-gray-600 text-sm">
                Service: <strong>{selectedPaymentRequest.service_name}</strong>
              </p>
              <p className="text-orange-600 font-bold text-lg mt-1">
                Amount: ₹{selectedPaymentRequest.custom_amount}
              </p>
            </div>

            {/* QR Code Display */}
            <div className="mb-6 bg-gray-50 p-4 rounded-lg border-2 border-gray-200">
              <img 
                src={selectedPaymentRequest.payment_link} 
                alt="Payment QR Code"
                className="w-full max-w-xs mx-auto"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <div style={{display: 'none'}} className="text-center text-red-600 p-4">
                Failed to load QR code. Please contact support.
              </div>
              <p className="text-center text-sm text-gray-600 mt-3">
                Scan with any UPI app (Google Pay, PhonePe, Paytm)
              </p>
            </div>

            {/* Transaction ID Input */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enter Transaction ID / UPI Reference Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={transactionId}
                onChange={(e) => setTransactionId(e.target.value)}
                placeholder="e.g., 123456789012"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                disabled={confirmingPayment}
              />
              <p className="text-xs text-gray-500 mt-1">
                After payment, enter the transaction ID shown in your UPI app
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={handleConfirmPayment}
                disabled={confirmingPayment || !transactionId.trim()}
                className="flex-1 bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
              >
                {confirmingPayment ? 'Confirming...' : '✓ Confirm Payment'}
              </button>
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  setTransactionId('');
                }}
                disabled={confirmingPayment}
                className="px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
              >
                Cancel
              </button>
            </div>

            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-800">
                <strong>Note:</strong> After confirming, your payment status will show "Under Review". 
                You'll be notified once verified by our team.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;